import * as Api from '@ViewModels';
import * as React from 'react';
import { useContactFilterQuery } from '../../../../queries/Contact';

export interface IPeopleInputContext {
	search: string;
	setSearch(search: string): void;
	onSearchChange(event: React.ChangeEvent<HTMLInputElement>): void;
}

export interface IPeopleSearchContext {
	filter: Api.IBulkContactsRequest;
	setFilter(filter: Api.IBulkContactsRequest): void;
	sort: Api.ISortDescriptor;
	setSort(sort: Api.ISortDescriptor): void;
	contacts: Api.IContact[];
	contactsQuery: ReturnType<typeof useContactFilterQuery>;
	selectionState: Api.ESelectionState;
	selectedContactIds: string[];
	setSelectionState(state: Api.ESelectionState): void;
	setSelectedContactIds(ids: string[]): void;
	excludedContactIds: string[];
	setExcludedContactIds(ids: string[]): void;
}

export const PeopleSearchContext = React.createContext<IPeopleSearchContext>(null);
export const usePeopleSearch = () => {
	const context = React.useContext(PeopleSearchContext);
	if (!context) {
		throw new Error('usePeopleSearch can only be used within the <PeopleContainer /> component');
	}
	return context;
};

export const getSearchFromCriteria = (criteria: Api.IContactFilterCriteria[]) => {
	const selected =
		criteria.find(x => x.property === Api.ContactFilterCriteriaProperty.All) ??
		criteria.find(x => x.property === Api.ContactFilterCriteriaProperty.Name) ??
		criteria.find(x => x.property === Api.ContactFilterCriteriaProperty.Company) ??
		criteria.find(x => x.property === Api.ContactFilterCriteriaProperty.Tag && !x.op);

	return selected?.value || '';
};

// Tabs
export type PeopleSearchCriteria = 'All' | 'Name' | 'Tag' | 'Company';

export const getCriteriaFromTab = (tab: PeopleSearchCriteria) => {
	switch (tab) {
		case 'All':
			return Api.ContactFilterCriteriaProperty.All;
		case 'Name':
			return Api.ContactFilterCriteriaProperty.Name;
		case 'Tag':
			return Api.ContactFilterCriteriaProperty.Tag;
		case 'Company':
			return Api.ContactFilterCriteriaProperty.Company;
		default:
			break;
	}
};

export const getSelectedItemFromCriteria = (criteria: Api.IContactFilterCriteria[]) => {
	const selected =
		criteria.find(x => x.property === Api.ContactFilterCriteriaProperty.All) ??
		criteria.find(x => x.property === Api.ContactFilterCriteriaProperty.Name) ??
		criteria.find(x => x.property === Api.ContactFilterCriteriaProperty.Company) ??
		criteria.find(x => x.property === Api.ContactFilterCriteriaProperty.Tag);

	switch (selected?.property) {
		case Api.ContactFilterCriteriaProperty.All:
			return 'All';
		case Api.ContactFilterCriteriaProperty.Name:
			return 'Name';
		case Api.ContactFilterCriteriaProperty.Tag:
			return 'Tag';
		case Api.ContactFilterCriteriaProperty.Company:
			return 'Company';
		default:
			return 'All';
	}
};

export const DefaultPeopleFilter: Api.IBulkContactsRequest = {
	filter: { criteria: [] },
	ownershipFilter: {
		criteria: { property: Api.ContactFilterCriteriaProperty.All },
	},
	includeContactIds: [],
};

export const DefaultPeopleSort: Api.ISortDescriptor = {
	sort: 'asc',
	sortBy: 'handle',
};

export const CONTACT_SORT_PREFERENCE_SUFFIX = '_ContactSortPreference';
